import { collection, query, where, getDocs, serverTimestamp, addDoc, updateDoc, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Nomes das coleções no Firestore
 */
const OPTIONS_COLLECTION_NAME = 'avaliacoes_opcoes';
const CONFIG_COLLECTION_NAME = 'avaliacoes_config';

/**
 * Busca todas as opções de avaliação ativas
 *
 * @returns {Promise<Array>} Lista de opções de avaliação ativas
 */
export const getActiveEvaluationOptions = async () => {
  try {
    const q = query(
      collection(db, OPTIONS_COLLECTION_NAME),
      where('active', '==', true)
    );

    const querySnapshot = await getDocs(q);
    const options = [];

    querySnapshot.forEach((doc) => {
      options.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Agrupa as opções por categoria
    const groupedOptions = {
      positive: options.filter(option => option.category === 'positive').map(option => option.text),
      improvement: options.filter(option => option.category === 'improvement').map(option => option.text)
    };

    return groupedOptions;
  } catch (error) {
    console.error('Erro ao buscar opções de avaliação:', error);
    // Retorna opções padrão em caso de erro
    return {
      positive: [
        'Conteúdo relevante',
        'Dinâmica das atividades',
        'Aplicabilidade no trabalho',
        'Materiais de apoio',
        'Duração',
        'Reprojetos audiovisuais',
        'Organização do treinamento',
      ],
      improvement: [
        'Mais exemplos práticos',
        'Melhor ritmo das explicações',
        'Material complementar mais detalhado',
        'Ajustar carga horária',
      ]
    };
  }
};

/**
 * Busca as opções de avaliação configuradas para um projeto específico
 *
 * @param {string} courseName - Nome do projeto
 * @returns {Promise<Object>} Opções configuradas para o projeto
 */
export const getEvaluationOptionsForCourse = async (courseName) => {
  try {
    // Primeiro tenta obter a configuração específica para este curso
    if (courseName) {
      const configRef = doc(db, CONFIG_COLLECTION_NAME, courseName);
      const configDoc = await getDoc(configRef);

      if (configDoc.exists()) {
        const configData = configDoc.data();
        if (configData.active !== false) { // Se a configuração estiver ativa
          return {
            positive: configData.positiveOptions || [],
            improvement: configData.improvementOptions || []
          };
        }
      }
    }

    // Se não houver configuração específica ou estiver inativa, retorna todas as opções ativas
    return await getActiveEvaluationOptions();
  } catch (error) {
    console.error('Erro ao buscar opções de avaliação para o projeto:', error);
    // Retorna opções padrão em caso de erro
    return {
      positive: [
        'Conteúdo relevante',
        'Dinâmica das atividades',
        'Aplicabilidade no trabalho',
        'Materiais de apoio',
        'Duração',
        'Reprojetos audiovisuais',
        'Organização do treinamento',
      ],
      improvement: [
        'Mais exemplos práticos',
        'Melhor ritmo das explicações',
        'Material complementar mais detalhado',
        'Ajustar carga horária',
      ]
    };
  }
};

/**
 * Busca as opções de avaliação configuradas para um ID de configuração específico
 * Agora também retorna courseName, userId e isScorm
 *
 * @param {string} configId - ID da configuração
 * @returns {Promise<Object>} Configuração completa incluindo opções, courseName, userId e isScorm
 */
export const getEvaluationOptionsByConfigId = async (configId) => {
  try {
    // Busca a configuração específica pelo ID
    if (configId) {
      const configRef = doc(db, CONFIG_COLLECTION_NAME, configId);
      const configDoc = await getDoc(configRef);

      if (configDoc.exists()) {
        const configData = configDoc.data();
        if (configData.active !== false) { // Se a configuração estiver ativa
          return {
            positive: configData.positiveOptions || [],
            improvement: configData.improvementOptions || [],
            courseName: configData.courseName || null,
            userId: configData.userId || null,
            isScorm: configData.isScorm || false,
            evaluationTitle: configData.evaluationTitle || 'O que você achou do treinamento?'
          };
        }
      }
    }

    // Se não houver configuração específica ou estiver inativa, retorna todas as opções ativas
    const activeOptions = await getActiveEvaluationOptions();
    return {
      ...activeOptions,
      courseName: null,
      userId: null,
      isScorm: false
    };
  } catch (error) {
    console.error('Erro ao buscar opções de avaliação para a configuração:', error);
    // Retorna opções padrão em caso de erro
    return {
      positive: [
        'Conteúdo relevante',
        'Dinâmica das atividades',
        'Aplicabilidade no trabalho',
        'Materiais de apoio',
        'Duração',
        'Reprojetos audiovisuais',
        'Organização do treinamento',
      ],
      improvement: [
        'Mais exemplos práticos',
        'Melhor ritmo das explicações',
        'Material complementar mais detalhado',
        'Ajustar carga horária',
      ],
      courseName: null,
      userId: null,
      isScorm: false,
      evaluationTitle: 'O que você achou do treinamento?'
    };
  }
};

/**
 * Busca todas as opções de avaliação (ativas e inativas)
 *
 * @returns {Promise<Array>} Lista de todas as opções de avaliação
 */
export const getAllEvaluationOptions = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, OPTIONS_COLLECTION_NAME));
    const options = [];

    querySnapshot.forEach((doc) => {
      options.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return options;
  } catch (error) {
    console.error('Erro ao buscar todas as opções de avaliação:', error);
    return [];
  }
};

/**
 * Adiciona uma nova opção de avaliação
 *
 * @param {Object} optionData - Dados da nova opção
 * @param {string} optionData.text - Texto da opção
 * @param {string} optionData.category - Categoria ('positive' ou 'improvement')
 * @param {boolean} [optionData.active] - Se a opção está ativa (padrão: true)
 * @returns {Promise<Object>} Objeto com sucesso e ID do documento
 */
export const addEvaluationOption = async (optionData) => {
  try {
    const dataToSave = {
      text: optionData.text,
      category: optionData.category,
      active: optionData.active !== undefined ? optionData.active : true,
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, OPTIONS_COLLECTION_NAME), dataToSave);

    // console.log('✅ Opção de avaliação adicionada com sucesso! ID:', docRef.id);

    return {
      success: true,
      id: docRef.id,
      message: 'Opção de avaliação adicionada com sucesso!',
    };
  } catch (error) {
    console.error('❌ Erro ao adicionar opção de avaliação:', error);

    return {
      success: false,
      error: error.message,
      message: 'Erro ao adicionar opção de avaliação. Tente novamente.',
    };
  }
};

/**
 * Atualiza uma opção de avaliação existente
 *
 * @param {string} optionId - ID da opção a ser atualizada
 * @param {Object} updateData - Dados para atualização
 * @returns {Promise<Object>} Objeto com status da operação
 */
export const updateEvaluationOption = async (optionId, updateData) => {
  try {
    const optionRef = doc(db, OPTIONS_COLLECTION_NAME, optionId);
    await updateDoc(optionRef, updateData);

    // console.log('✅ Opção de avaliação atualizada com sucesso! ID:', optionId);

    return {
      success: true,
      message: 'Opção de avaliação atualizada com sucesso!',
    };
  } catch (error) {
    console.error('❌ Erro ao atualizar opção de avaliação:', error);

    return {
      success: false,
      error: error.message,
      message: 'Erro ao atualizar opção de avaliação. Tente novamente.',
    };
  }
};

/**
 * Remove uma opção de avaliação (define como inativa)
 *
 * @param {string} optionId - ID da opção a ser removida
 * @returns {Promise<Object>} Objeto com status da operação
 */
export const removeEvaluationOption = async (optionId) => {
  try {
    const optionRef = doc(db, OPTIONS_COLLECTION_NAME, optionId);
    await updateDoc(optionRef, { active: false });

    // console.log('✅ Opção de avaliação removida com sucesso! ID:', optionId);

    return {
      success: true,
      message: 'Opção de avaliação removida com sucesso!',
    };
  } catch (error) {
    console.error('❌ Erro ao remover opção de avaliação:', error);

    return {
      success: false,
      error: error.message,
      message: 'Erro ao remover opção de avaliação. Tente novamente.',
    };
  }
};

export default {
  getActiveEvaluationOptions,
  getEvaluationOptionsForCourse,
  getEvaluationOptionsByConfigId,
  getAllEvaluationOptions,
  addEvaluationOption,
  updateEvaluationOption,
  removeEvaluationOption,
};